import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getCurrentOrganization,
  updateCurrentOrganization,
} from '../../services/organizationService';
import type { OrganizationDetails } from '../../types/organization';
import {
  isConcurrencyConflict,
  organizationErrorMessage,
} from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationEdit'>;

type FormState = {
  organizationName: string;
  legalName: string;
  registrationNumber: string;
  taxRegistrationNumber: string;
  officialEmail: string;
  phoneNumber: string;
  websiteUrl: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateName: string;
  countryCode: string;
  postalCode: string;
  timeZone: string;
  defaultCurrencyCode: string;
  defaultDateFormat: string;
  defaultTimeFormat: string;
  logoUrl: string;
};

function fromOrg(org: OrganizationDetails): FormState {
  return {
    organizationName: org.organizationName,
    legalName: org.legalName ?? '',
    registrationNumber: org.registrationNumber ?? '',
    taxRegistrationNumber: org.taxRegistrationNumber ?? '',
    officialEmail: org.officialEmail ?? '',
    phoneNumber: org.phoneNumber ?? '',
    websiteUrl: org.websiteUrl ?? '',
    addressLine1: org.addressLine1 ?? '',
    addressLine2: org.addressLine2 ?? '',
    city: org.city ?? '',
    stateName: org.stateName ?? '',
    countryCode: org.countryCode,
    postalCode: org.postalCode ?? '',
    timeZone: org.timeZone,
    defaultCurrencyCode: org.defaultCurrencyCode,
    defaultDateFormat: org.defaultDateFormat,
    defaultTimeFormat: org.defaultTimeFormat,
    logoUrl: org.logoUrl ?? '',
  };
}

export function validateOrganizationForm(form: FormState): string | null {
  if (!form.organizationName.trim()) return 'Organization name is required.';
  if (form.organizationName.trim().length > 200) {
    return 'Organization name is too long.';
  }
  if (form.officialEmail.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.officialEmail.trim())) {
      return 'Official email is invalid.';
    }
  }
  if (form.countryCode.trim().length !== 2) {
    return 'Country code must be exactly two letters.';
  }
  if (!form.timeZone.trim()) return 'Timezone is required.';
  if (form.defaultCurrencyCode.trim().length !== 3) {
    return 'Currency code must be exactly three letters.';
  }
  if (!form.defaultDateFormat.trim()) return 'Date format is required.';
  if (!form.defaultTimeFormat.trim()) return 'Time format is required.';
  if (form.phoneNumber.trim().length > 30) return 'Phone number is too long.';
  if (form.postalCode.trim().length > 20) return 'Postal code is too long.';
  if (form.websiteUrl.trim().length > 500) return 'Website URL is too long.';
  return null;
}

export function EditOrganizationScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const [org, setOrg] = useState<OrganizationDetails | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCurrentOrganization();
      setOrg(data);
      setForm(fromOrg(data));
      dirty.current = false;
      setError(null);
      setFieldError(null);
    } catch (caught) {
      setOrg(null);
      setForm(null);
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => (current ? { ...current, [key]: value } : current));
  }

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  async function submit() {
    if (!form || !org || savingLock.current) return;
    const validation = validateOrganizationForm(form);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);
    try {
      await runOperation('update', () => updateCurrentOrganization({
        organizationName: form.organizationName,
        legalName: form.legalName,
        registrationNumber: form.registrationNumber,
        taxRegistrationNumber: form.taxRegistrationNumber,
        officialEmail: form.officialEmail,
        phoneNumber: form.phoneNumber,
        websiteUrl: form.websiteUrl,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        stateName: form.stateName,
        countryCode: form.countryCode,
        postalCode: form.postalCode,
        timeZone: form.timeZone,
        defaultCurrencyCode: form.defaultCurrencyCode,
        defaultDateFormat: form.defaultDateFormat,
        defaultTimeFormat: form.defaultTimeFormat,
        logoUrl: form.logoUrl,
        statusId: org.statusId,
        rowVersion: org.rowVersion,
      }));
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught)) {
        const message = organizationErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          organizationErrorMessage(caught, 'Unable to update organization.'),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.ORGANIZATION_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !form ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit organization"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading organization" />
        </AppScreen>
      ) : error && !form ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit organization"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : form && org ? (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title="Edit organization"
            onBack={navigation.goBack}
          />
          <View style={styles.body}>
          <AppCard style={styles.card}>
            <Text style={themed.readOnlyLabel}>Organization code</Text>
            <Text style={themed.readOnlyValue}>
              {org.organizationCode || 'Not listed'}
            </Text>
            <AppInput
              label="Organization name *"
              value={form.organizationName}
              onChangeText={value => setField('organizationName', value)}
            />
            <AppInput
              label="Legal name"
              value={form.legalName}
              onChangeText={value => setField('legalName', value)}
            />
            <AppInput
              label="Registration number"
              value={form.registrationNumber}
              onChangeText={value => setField('registrationNumber', value)}
            />
            <AppInput
              label="Tax registration number"
              value={form.taxRegistrationNumber}
              onChangeText={value => setField('taxRegistrationNumber', value)}
            />
            <AppInput
              label="Official email"
              value={form.officialEmail}
              onChangeText={value => setField('officialEmail', value)}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <AppInput
              label="Phone number"
              value={form.phoneNumber}
              onChangeText={value => setField('phoneNumber', value)}
              keyboardType="phone-pad"
            />
            <AppInput
              label="Website URL"
              value={form.websiteUrl}
              onChangeText={value => setField('websiteUrl', value)}
              autoCapitalize="none"
            />
            <AppInput
              label="Address line 1"
              value={form.addressLine1}
              onChangeText={value => setField('addressLine1', value)}
            />
            <AppInput
              label="Address line 2"
              value={form.addressLine2}
              onChangeText={value => setField('addressLine2', value)}
            />
            <AppInput
              label="City"
              value={form.city}
              onChangeText={value => setField('city', value)}
            />
            <AppInput
              label="State"
              value={form.stateName}
              onChangeText={value => setField('stateName', value)}
            />
            <AppInput
              label="Country code * (2 letters)"
              value={form.countryCode}
              onChangeText={value => setField('countryCode', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Postal code"
              value={form.postalCode}
              onChangeText={value => setField('postalCode', value)}
            />
            <AppInput
              label="Timezone *"
              value={form.timeZone}
              onChangeText={value => setField('timeZone', value)}
              autoCapitalize="none"
            />
            <AppInput
              label="Currency code * (3 letters)"
              value={form.defaultCurrencyCode}
              onChangeText={value => setField('defaultCurrencyCode', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Date format *"
              value={form.defaultDateFormat}
              onChangeText={value => setField('defaultDateFormat', value)}
              autoCapitalize="characters"
            />
            <AppInput
              label="Time format *"
              value={form.defaultTimeFormat}
              onChangeText={value => setField('defaultTimeFormat', value)}
              autoCapitalize="characters"
            />
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <View style={styles.actions}>
              <AppButton title="Save" onPress={submit} loading={saving} />
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={confirmLeave}
              />
            </View>
          </AppCard>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  actions: { gap: spacing.sm },
});
